import React, { useState, useEffect, useRef, useCallback } from "react";
import CreatableSelect from "react-select/creatable";
import { formatDate, renderCustomPagination } from "./candidateHelpers";
import CandidateTableBody from "./CandidateTableBody";

const selectStyles = {
    control: (base) => ({
        ...base,
        width: 180,
        minWidth: 180,
        borderRadius: "8px",
        fontSize: "13px",
        fontFamily: '"Inter", sans-serif',
        borderColor: "#E4E9F0",
        boxShadow: "none",
        "&:hover": { borderColor: "#C4CAD3" },
    }),
    menu: (base) => ({ ...base, minWidth: 180, fontFamily: '"Inter", sans-serif', fontSize: "13px" }),
    menuList: (base) => ({ ...base, overflowX: "hidden" }),
    placeholder: (base) => ({ ...base, color: "#8A9199", fontFamily: '"Inter", sans-serif' }),
    option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif', fontSize: "13px" }),
    singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
};

const ListTab = ({
    folderOptions,
    folderLoading,
    selectedFolder,
    onFolderChange,
    listOptions,
    listLoading,
    selectedListView,
    listViewCandidates,
    listViewLoading,
    listViewPagination,
    onListViewPageChange,
    onListSelect,
    onViewSummary,
    onDownload,
    onAddClick,
    getListsByFolder,
}) => {
    const [searchText, setSearchText] = useState("");
    const [searchDisabled, setSearchDisabled] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        setSearchDisabled(listLoading);
    }, [listLoading]);

    useEffect(() => {
        setSearchText("");
    }, [selectedFolder]);

    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setSearchText(value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (getListsByFolder) {
                    getListsByFolder({ name: value, folder_id: selectedFolder });
                }
            }, 500);
        },
        [selectedFolder, getListsByFolder]
    );

    const [listPage, setListPage] = useState(1);
    const [listPageSize, setListPageSize] = useState(10);
    const [candPageSize, setCandPageSize] = useState(10);
    const [candSearchText, setCandSearchText] = useState("");
    const candDebounceRef = useRef(null);

    useEffect(() => { setListPage(1); }, [selectedFolder]);
    useEffect(() => { setCandPageSize(10); setCandSearchText(""); }, [selectedListView]);

    const handleCandSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setCandSearchText(value);
            if (candDebounceRef.current) clearTimeout(candDebounceRef.current);
            candDebounceRef.current = setTimeout(() => {
                if (onListViewPageChange) onListViewPageChange({ limit: candPageSize, page: 1, search: value });
            }, 500);
        },
        [candPageSize, onListViewPageChange]
    );

    // ── Candidate view inside a selected list ────────────────────────────────
    if (selectedListView) {
        const total = listViewPagination?.total || 0;
        const currentPage = listViewPagination?.page || 1;
        const totalCandPages = Math.ceil(total / candPageSize);
        const candStart = total > 0 ? (currentPage - 1) * candPageSize + 1 : 0;
        const candEnd = Math.min(currentPage * candPageSize, total);

        return (
            <div style={{margin:'0 12px'}}>
                <div className="cv-controls-bar">
                    <div className="cv-search-wrap">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="cv-search-icon">
                            <circle cx="11" cy="11" r="8" stroke="#8A9199" strokeWidth="2" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="#8A9199" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        <input
                            type="text"
                            className="cv-search-input"
                            placeholder="Search candidates..."
                            value={candSearchText}
                            onChange={handleCandSearchChange}
                        />
                    </div>
                </div>

                <div className="cv-table-outer">
                    <div className="cv-table-scroll">
                        <table className="cv-table">
                            <thead>
                                <tr>
                                    <th className="th-name">Name</th>
                                    <th className="th-center">Email</th>
                                    <th className="th-center">Designation</th>
                                    <th className="th-center">Added On</th>
                                    <th className="th-skills">Skills</th>
                                    <th className="th-center">List Name</th>
                                    <th className="th-actions">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listViewLoading ? (
                                    <tr>
                                        <td colSpan={7} className="td-loading">
                                            <div className="spinner-border cv-spinner" role="status">
                                                <span className="visually-hidden">Loading candidates...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <CandidateTableBody
                                        candidates={listViewCandidates}
                                        onViewSummary={onViewSummary}
                                        onDownload={onDownload}
                                        onAddClick={onAddClick}
                                    />
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!listViewLoading && total > 0 && (
                        <div className="cv-pagination">
                            <span className="cv-pagination-info">
                                Showing {candStart}–{candEnd} of <strong>{total}</strong> Candidates
                            </span>
                            <div className="cv-pagination-right">
                                <div className="cv-page-btns">
                                    {renderCustomPagination(totalCandPages, currentPage, (p) => {
                                        if (onListViewPageChange) onListViewPageChange({ limit: candPageSize, page: p, search: candSearchText });
                                    })}
                                </div>
                                <div className="cv-per-page">
                                    <span>Per page:</span>
                                    <select
                                        value={candPageSize}
                                        onChange={(e) => {
                                            const newSize = parseInt(e.target.value);
                                            setCandPageSize(newSize);
                                            if (onListViewPageChange) onListViewPageChange({ limit: newSize, page: 1, search: candSearchText });
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Lists view ────────────────────────────────────────────────────────────
    const totalListPages = Math.ceil((listOptions.length || 0) / listPageSize);
    const pagedLists = listOptions.slice((listPage - 1) * listPageSize, listPage * listPageSize);
    const listStart = (listPage - 1) * listPageSize + 1;
    const listEnd = Math.min(listPage * listPageSize, listOptions.length);

    return (
        <div style={{margin:'0 12px'}}>
            <div className="cv-controls-bar">
                <div className="cv-search-wrap">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="cv-search-icon">
                        <circle cx="11" cy="11" r="8" stroke="#8A9199" strokeWidth="2" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="#8A9199" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                        type="text"
                        className="cv-search-input"
                        placeholder="Search lists..."
                        value={searchText}
                        disabled={searchDisabled}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="cv-filter-group">
                    <CreatableSelect
                        className="select-btn"
                        inputId="folder-filter"
                        value={folderOptions.find((f) => f.value === selectedFolder) || null}
                        onChange={(option) => onFolderChange(option?.value ?? "")}
                        options={folderOptions}
                        isLoading={folderLoading}
                        isDisabled={folderLoading}
                        isClearable
                        placeholder="All Folders"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />
                </div>
            </div>

            <div className="cv-table-outer">
                <div className="cv-table-scroll">
                    <table className="cv-table">
                        <thead>
                            <tr>
                                <th className="th-name">Lists</th>
                                <th className="th-center">Folder</th>
                                <th className="th-center">Candidates</th>
                                <th className="th-center">Created On</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listLoading ? (
                                <tr>
                                    <td colSpan={4} className="td-loading">
                                        <div className="spinner-border cv-spinner" role="status">
                                            <span className="visually-hidden">Loading lists...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : listOptions.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="td-empty">
                                        {selectedFolder ? (
                                            <>
                                                No lists yet. Click{" "}
                                                <strong className="cv-empty-link" onClick={onAddClick}>
                                                    Add Candidates
                                                </strong>{" "}
                                                to get started.
                                            </>
                                        ) : (
                                            "Please select a folder to get started."
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                pagedLists.map((c) => (
                                    <tr key={c.id}>
                                        <td className="td-name">
                                            <span
                                                className="cv-list-name"
                                                onClick={() => onListSelect(c)}
                                            >
                                                {c.name}
                                            </span>
                                        </td>
                                        <td className="td-center">
                                            {c.folder?.name || <span className="cv-na-text">N/A</span>}
                                        </td>
                                        <td className="td-center">{c.candidates_count || 0}</td>
                                        <td className="td-center">{formatDate(c.created_at)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!listLoading && listOptions.length > 0 && (
                    <div className="cv-pagination">
                        <span className="cv-pagination-info">
                            Showing {listStart}–{listEnd} of <strong>{listOptions.length}</strong> Lists
                        </span>
                        <div className="cv-pagination-right">
                            <div className="cv-page-btns">
                                {renderCustomPagination(totalListPages, listPage, setListPage)}
                            </div>
                            <div className="cv-per-page">
                                <span>Per page:</span>
                                <select
                                    value={listPageSize}
                                    onChange={(e) => {
                                        setListPageSize(parseInt(e.target.value));
                                        setListPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ListTab;
